import { BodyText } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useState } from "react";
import { EditableRecordButton } from "../../components/EditableRecordButton";
import {
  type ExternalCareTeamMember,
  useRemoveExternalCareTeamMember,
  useUpdateExternalCareTeamMember,
} from "../network/useExternalCareTeam";
import { MemberForm } from "./MemberForm";

interface CareTeamMemberProps {
  patientId: string;
  careTeamId: string;
  careTeamMember: ExternalCareTeamMember;
}

export const CareTeamMember = ({
  patientId,
  careTeamId,
  careTeamMember,
}: CareTeamMemberProps) => {
  const { mutate: removeMember, isPending: isRemovingMember } =
    useRemoveExternalCareTeamMember(patientId);
  const { mutate: updateMember, isPending: isUpdatingMember } =
    useUpdateExternalCareTeamMember({
      patientId,
      onSuccess: () => setEditing(false),
    });
  const [editing, setEditing] = useState(false);

  const handleUpdateMember = async (newDetails: {
    name: string;
    npi?: string;
    specialtyCode: string;
    specialtyCodeDisplay: string;
    specialtyCodeSystem: string;
  }) => {
    trackEvent(TRACKERS.SAVE_CARE_TEAM);
    updateMember({
      careTeamId: careTeamId,
      existingMember: {
        nameFullFormatted: careTeamMember.nameFullFormatted,
        npi: careTeamMember.npi || undefined,
        specialtyCode: {
          code: careTeamMember.specialtyCode.code,
          display: careTeamMember.specialtyCode.display,
          system: careTeamMember.specialtyCode.system,
        },
      },
      newMember: {
        nameFullFormatted: newDetails.name,
        npi: newDetails.npi || undefined,
        specialtyCode: {
          code: newDetails.specialtyCode,
          display: newDetails.specialtyCodeDisplay,
          system: newDetails.specialtyCodeSystem,
        },
      },
    });
  };

  return editing ? (
    <MemberForm
      onSubmit={(newDetails) => handleUpdateMember(newDetails)}
      onCancel={() => setEditing(false)}
      isSaving={isRemovingMember || isUpdatingMember}
      existingMember={careTeamMember}
    />
  ) : (
    <EditableRecordButton
      onClick={() => setEditing(true)}
      onDelete={() =>
        removeMember({
          careTeamId,
          name: careTeamMember.nameFullFormatted,
          npi: careTeamMember.npi,
        })
      }
      deletePending={isRemovingMember || isUpdatingMember}
    >
      <div
        key={careTeamMember.nameFullFormatted}
        className="flex flex-row w-full gap-2"
      >
        <div className="flex flex-col flex-1 gap-2">
          <BodyText size="sm" bold textColor="secondary">
            Name
          </BodyText>
          <BodyText size="sm" textColor="secondary">
            {careTeamMember.nameFullFormatted}
          </BodyText>
        </div>
        <div className="flex flex-col flex-1 gap-2">
          <BodyText size="sm" bold textColor="secondary">
            Specialty
          </BodyText>
          <BodyText size="sm" textColor="secondary">
            {careTeamMember.specialtyCode.display}
          </BodyText>
        </div>
      </div>
    </EditableRecordButton>
  );
};
